import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth.jsx';
import { FamilyProvider, useFamily } from './lib/family.jsx';
import SignIn from './routes/SignIn.jsx';
import Setup from './routes/Setup.jsx';
import Onboarding from './routes/Onboarding.jsx';
import FamilyHome from './routes/FamilyHome.jsx';
import Children from './routes/Children.jsx';
import Switcher from './routes/Switcher.jsx';
import GaugePreview from './routes/GaugePreview.jsx';
import FuelSetup from './routes/FuelSetup.jsx';
import Tiles from './routes/Tiles.jsx';
import Invite from './routes/Invite.jsx';
import Bank from './routes/Bank.jsx';
import ChildBank from './routes/ChildBank.jsx';
import SavingsRule from './routes/SavingsRule.jsx';
import AllowanceRule from './routes/AllowanceRule.jsx';
import Join, { ApplyPendingInvite } from './routes/Join.jsx';
import Wishlist from './routes/Wishlist.jsx';
import WishQueue from './routes/WishQueue.jsx';
import HouseRules from './routes/HouseRules.jsx';
import Try from './routes/Try.jsx';
import Family from './routes/Family.jsx';
import TabBar from './ui/TabBar.jsx';
import FridgeSheet from './routes/FridgeSheet.jsx';
import { rememberInviteCode } from './lib/invite.js';

function Loading({ label = 'Loading' }) {
  return <div className="app-shell"><p className="muted" role="status">{label}…</p></div>;
}

/** A family is the precondition for every screen that isn't onboarding. */
function RequireFamily({ children }) {
  const { family, loading } = useFamily();
  if (loading) return <Loading />;
  if (!family) return <Navigate to="/welcome" replace />;
  return children;
}

/**
 * Every signed-in family screen, with the tab bar under it.
 *
 * The bar is a layout route rather than something each screen renders, so a
 * screen cannot forget it and no screen can show a different one. What is
 * *not* under here is deliberate: onboarding and invite redemption have no
 * family to navigate yet, the profile switcher is a doorway rather than a
 * place, and the fridge sheet is for paper.
 */
function WithTabs() {
  return (
    <RequireFamily>
      <Outlet />
      <TabBar />
    </RequireFamily>
  );
}

export default function App() {
  const { user, loading, isConfigured } = useAuth();
  const path = window.location.pathname;
  const previewing = path === '/preview/gauge';
  const trying = path === '/try';

  // The gauge preview is pure domain and pure component, so it must not sit
  // behind auth or a database. Being able to look at the UI on a fresh clone is
  // the point of it.
  if (previewing) return <GaugePreview />;

  // Trial mode is backend-free by definition, so it sits in front of both the
  // auth check and the Supabase check. With no backend configured it is also
  // the only thing there is to do, so that is where a visitor lands.
  if (path === '/setup') return <Setup />;
  if (trying || !isConfigured) return <Try />;
  if (loading) return <Loading label="Checking your session" />;

  // An invited guardian arrives at /join with no session, and the sign-in
  // screen is below this line — so the code is put somewhere safe on the way
  // past. It has to survive a round trip through the mail, because the magic
  // link comes back to the origin and not to /join.
  if (path === '/join') rememberInviteCode(new URLSearchParams(window.location.search).get('code'));

  if (!user) return <SignIn />;

  return (
    <FamilyProvider>
      {/* Holds a stashed invite in front of every route: a guardian who has
          just come back from their email lands on `/`, not on /join. */}
      <ApplyPendingInvite>
        <Routes>
          <Route path="/welcome" element={<Onboarding />} />
          {/* Deliberately outside RequireFamily. Redeeming an invite is how the
              invited guardian gets a family at all, so a family cannot be its
              precondition — RequireFamily would bounce them to /welcome and
              offer to start a second household instead. */}
          <Route path="/join" element={<Join />} />
          <Route path="/switch" element={<RequireFamily><Switcher /></RequireFamily>} />
          <Route path="/fuel/print" element={<RequireFamily><FridgeSheet /></RequireFamily>} />
          <Route element={<WithTabs />}>
            <Route path="/" element={<FamilyHome />} />
            <Route path="/family" element={<Family />} />
            <Route path="/children" element={<Children />} />
            <Route path="/invite" element={<Invite />} />
            {/* The child ledger. `/bank` is the parent's overview and redirects a
                child profile to their own page; the two child-scoped routes serve
                both views and decide for themselves what each may do. */}
            <Route path="/bank" element={<Bank />} />
            <Route path="/bank/:childId" element={<ChildBank />} />
            <Route path="/bank/:childId/savings" element={<SavingsRule />} />
            {/* Both rule screens bounce a child profile home themselves: what a
                household pays is a grown-up's decision, and a child who can set
                their own allowance or bonus rate has a money printer. */}
            <Route path="/bank/:childId/allowance" element={<AllowanceRule />} />
            {/* The waiting room. `/wishes` is the parent's queue across every
                child and sends a child profile to their own list; the settings
                screen bounces one home itself, because a child who could shorten
                their own cooldown has no cooldown. */}
            <Route path="/wishes" element={<WishQueue />} />
            <Route path="/wishes/settings" element={<HouseRules />} />
            <Route path="/wishes/:childId" element={<Wishlist />} />
            <Route path="/fuel/setup" element={<FuelSetup />} />
            <Route path="/fuel/tiles" element={<Tiles />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ApplyPendingInvite>
    </FamilyProvider>
  );
}
